import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { LiveRegionProvider } from '../ui/accessibility/live-region';

interface AccessibilitySettings {
  highContrast: boolean;
  largeText: boolean;
  reduceMotion: boolean;
  keyboardNavigation: boolean;
  screenReaderOptimized: boolean;
  focusVisible: boolean;
  underlineLinks: boolean;
  dyslexiaFriendlyFont: boolean;
  stopMediaAutoplay: boolean;
  fontScale: number;
  lineHeight: number;
  letterSpacing: number;
  contrastLevel: number;
  saturationLevel: number;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => void;
  updateSettings: (updates: Partial<AccessibilitySettings>) => void;
  resetSettings: () => void;
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  focusElement: (elementId: string) => void;
  isKeyboardNavActive: boolean;
  setKeyboardNavActive: (active: boolean) => void;
}

const ACCESSIBILITY_SETTINGS_STORAGE_KEY = 'accessibility-settings-v2';

const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  highContrast: false,
  largeText: false,
  reduceMotion: false,
  keyboardNavigation: true,
  screenReaderOptimized: false,
  focusVisible: true,
  underlineLinks: false,
  dyslexiaFriendlyFont: false,
  stopMediaAutoplay: false,
  fontScale: 100,
  lineHeight: 160,
  letterSpacing: 0,
  contrastLevel: 100,
  saturationLevel: 100,
};

function readSavedSettings(): AccessibilitySettings {
  if (typeof window === 'undefined') return DEFAULT_ACCESSIBILITY_SETTINGS;

  try {
    const raw = localStorage.getItem(ACCESSIBILITY_SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_ACCESSIBILITY_SETTINGS;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_ACCESSIBILITY_SETTINGS;

    return {
      ...DEFAULT_ACCESSIBILITY_SETTINGS,
      ...parsed,
    };
  } catch {
    return DEFAULT_ACCESSIBILITY_SETTINGS;
  }
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

// Create hook function separately for HMR stability
function createUseAccessibility() {
  return () => {
    const context = useContext(AccessibilityContext);
    if (!context) {
      throw new Error('useAccessibility must be used within an AccessibilityProvider');
    }
    return context;
  };
}

export const useAccessibility = createUseAccessibility();

interface AccessibilityProviderProps {
  children: ReactNode;
}

export function AccessibilityProvider({ children }: AccessibilityProviderProps) {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => readSavedSettings());

  const [isKeyboardNavActive, setKeyboardNavActive] = useState(false);
  const [announcements, setAnnouncements] = useState<Array<{ message: string; priority: 'polite' | 'assertive'; id: string }>>([]);

  const updateSetting = <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateSettings = (updates: Partial<AccessibilitySettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_ACCESSIBILITY_SETTINGS);
  };

  const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const id = Date.now().toString();
    setAnnouncements(prev => [...prev, { message, priority, id }]);
    
    // Remove announcement after a delay to prevent accumulation
    setTimeout(() => {
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    }, 1000);
  };

  const focusElement = (elementId: string) => {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.focus();
      }
    }, 100);
  };

  useEffect(() => {
    try {
      localStorage.setItem(ACCESSIBILITY_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Best effort only.
    }
  }, [settings]);

  // Apply accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // High contrast mode
    if (settings.highContrast) {
      root.classList.add('high-contrast-mode');
    } else {
      root.classList.remove('high-contrast-mode');
    }

    // Large text mode
    if (settings.largeText) {
      root.classList.add('large-text-mode');
    } else {
      root.classList.remove('large-text-mode');
    }

    // Reduce motion
    if (settings.reduceMotion) {
      root.classList.add('no-animations');
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('no-animations');
      root.classList.remove('reduced-motion');
    }

    // Keyboard navigation
    if (settings.keyboardNavigation) {
      root.classList.add('keyboard-nav-active');
    } else {
      root.classList.remove('keyboard-nav-active');
    }

    // Screen reader optimized
    if (settings.screenReaderOptimized) {
      root.classList.add('screen-reader-optimized');
    } else {
      root.classList.remove('screen-reader-optimized');
    }

    // Focus visible
    if (settings.focusVisible) {
      root.classList.add('focus-visible-active');
    } else {
      root.classList.remove('focus-visible-active');
    }

    root.classList.toggle('a11y-underline-links', settings.underlineLinks);
    root.classList.toggle('a11y-dyslexia-font', settings.dyslexiaFriendlyFont);
    root.classList.toggle('a11y-stop-autoplay', settings.stopMediaAutoplay);

    root.style.setProperty('--a11y-font-scale', `${settings.fontScale / 100}`);
    root.style.setProperty('--a11y-line-height', `${settings.lineHeight / 100}`);
    root.style.setProperty('--a11y-letter-spacing', `${settings.letterSpacing}px`);
    root.style.setProperty('--a11y-contrast', `${settings.contrastLevel}%`);
    root.style.setProperty('--a11y-saturation', `${settings.saturationLevel}%`);

    // Apply contrast and saturation globally without overriding app-level theming.
    // IMPORTANT: any non-`none` `filter` on <body> makes it the containing block for
    // all position:fixed descendants (including portaled modals/lightboxes), which
    // pins them to the top of the document instead of the viewport. Only apply the
    // filter when it actually changes rendering, and clear it at defaults.
    if (settings.contrastLevel !== 100 || settings.saturationLevel !== 100) {
      body.style.filter = `contrast(${settings.contrastLevel}%) saturate(${settings.saturationLevel}%)`;
    } else {
      body.style.filter = '';
    }
  }, [settings]);

  useEffect(() => {
    if (!settings.stopMediaAutoplay) return;

    const pauseMedia = (node: ParentNode) => {
      const mediaElements = node.querySelectorAll('video, audio');
      mediaElements.forEach((media) => {
        const element = media as HTMLMediaElement;
        element.autoplay = false;
        if (!element.paused) {
          void element.pause();
        }
      });
    };

    pauseMedia(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((addedNode) => {
          if (!(addedNode instanceof HTMLElement)) return;

          if (addedNode.matches('video, audio')) {
            const media = addedNode as HTMLMediaElement;
            media.autoplay = false;
            if (!media.paused) {
              void media.pause();
            }
            return;
          }

          pauseMedia(addedNode);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [settings.stopMediaAutoplay]);

  // Keyboard navigation detection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setKeyboardNavActive(true);
      }
    };

    const handleMouseDown = () => {
      setKeyboardNavActive(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const value: AccessibilityContextType = {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    announceToScreenReader,
    focusElement,
    isKeyboardNavActive,
    setKeyboardNavActive,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      <LiveRegionProvider>
        {children}
        {/* Live regions for screen reader announcements */}
        <div className="live-region">
          {announcements.map(announcement => (
            <div
              key={announcement.id}
              role="status"
              aria-live={announcement.priority}
              aria-atomic="true"
            >
              {announcement.message}
            </div>
          ))}
        </div>
      </LiveRegionProvider>
    </AccessibilityContext.Provider>
  );
}